import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { RootState } from '../store/store';
import {
  UserRole,
  FormSubmission,
  FormSubmissionStatus,
  FormSubmissionStats,
  FormDirection,
} from '../types';
import { formSubmissionsService } from '../services/formSubmissionsService';

export const FormSubmissionsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);

  const [submissions, setSubmissions] = useState<FormSubmission[]>([]);
  const [stats, setStats] = useState<FormSubmissionStats>({ pending: 0, approved: 0, rejected: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<FormSubmissionStatus | ''>(FormSubmissionStatus.PENDING);
  const [retryingId, setRetryingId] = useState<string | null>(null);


  const canView = user?.role === UserRole.MANAGER ||
                  user?.role === UserRole.ORG_ADMIN ||
                  user?.role === UserRole.PLATFORM_ADMIN;

  const handleRetryTransfer = async (id: string) => {
    setRetryingId(id);
    try {
      await formSubmissionsService.retryTransfer(id);
      toast.success(t('formSubmissions.transferCreated'));
      loadData();
    } catch {
      toast.error(t('formSubmissions.transferCreateFailed'));
    } finally {
      setRetryingId(null);
    }
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [subs, statsData] = await Promise.all([
        formSubmissionsService.getAll(statusFilter || undefined),
        formSubmissionsService.getStats(),
      ]);
      setSubmissions(subs);
      setStats(statsData);
    } catch {
      toast.error(t('formSubmissions.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, t]);

  useEffect(() => {
    if (canView) {
      loadData();
    }
  }, [canView, loadData]);


  if (!canView) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{t('errors.accessDenied')}</h2>
        <p className="text-gray-500 dark:text-gray-400 mt-2">{t('errors.noPermission')}</p>
      </div>
    );
  }

  const getStatusBadge = (status: FormSubmissionStatus) => {
    const colors: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      approved: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      rejected: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[status]}`}>
        {t(`formSubmissions.${status}`)}
      </span>
    );
  };

  const getDirectionBadge = (direction: FormDirection) => {
    const isArrival = direction === FormDirection.ARRIVAL;
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
        isArrival
          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
          : 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
      }`}>
        {isArrival ? t('formSubmissions.arrival') : t('formSubmissions.departure')}
      </span>
    );
  };

  const formatDate = (dateStr: string) => {
    try {
      return format(new Date(dateStr), 'dd.MM.yyyy');
    } catch {
      return dateStr;
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('formSubmissions.title')}</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">{t('formSubmissions.subtitle')}</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('formSubmissions.stats.pending')}</p>
          <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{stats.pending}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('formSubmissions.stats.approved')}</p>
          <p className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.approved}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('formSubmissions.stats.rejected')}</p>
          <p className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.rejected}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('formSubmissions.stats.total')}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
        </div>
      </div>

      {/* Status Filter Tabs */}
      <div className="flex gap-2 mb-4 overflow-x-auto">
        {[
          { value: '', label: t('formSubmissions.all') },
          { value: FormSubmissionStatus.PENDING, label: t('formSubmissions.pending') },
          { value: FormSubmissionStatus.APPROVED, label: t('formSubmissions.approved') },
          { value: FormSubmissionStatus.REJECTED, label: t('formSubmissions.rejected') },
        ].map((tab) => (
          <button
            key={tab.value}
            onClick={() => setStatusFilter(tab.value as any)}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              statusFilter === tab.value
                ? 'bg-blue-600 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            {tab.label}
            {tab.value === FormSubmissionStatus.PENDING && stats.pending > 0 && (
              <span className="ml-2 bg-yellow-400 text-yellow-900 text-xs px-1.5 py-0.5 rounded-full">{stats.pending}</span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">{t('common.loading')}</div>
      ) : submissions.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400">{t('formSubmissions.noSubmissions')}</p>
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="hidden md:block bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('formSubmissions.filledBy')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('formSubmissions.filledBy')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('formSubmissions.externalRefNo')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('formSubmissions.date')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('formSubmissions.time')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('formSubmissions.direction')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('formSubmissions.airport')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('formSubmissions.location')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('formSubmissions.guestCount')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('common.status')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {submissions.map((sub) => (
                  <tr key={sub.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{sub.filledBy || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{sub.filledBy || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{sub.externalRefNo || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{formatDate(sub.transferDate)}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{sub.transferTime}</td>
                    <td className="px-4 py-3">{getDirectionBadge(sub.direction)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{sub.airport}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 max-w-[200px] truncate">{sub.location}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{sub.guestCount}</td>
                    <td className="px-4 py-3">{getStatusBadge(sub.status)}</td>
                    <td className="px-4 py-3">
                      {sub.createdTransferId ? (
                        <button
                          onClick={() => navigate(`/app/transfers?view=${sub.createdTransferId}`)}
                          className="text-sm text-blue-600 dark:text-blue-400 hover:underline font-medium"
                        >
                          {t('formSubmissions.viewTransfer')}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleRetryTransfer(sub.id)}
                          disabled={retryingId === sub.id}
                          className="text-sm text-orange-600 dark:text-orange-400 hover:underline font-medium disabled:opacity-50"
                        >
                          {retryingId === sub.id ? t('common.loading') : t('formSubmissions.createTransfer')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-3">
            {submissions.map((sub) => (
              <div key={sub.id} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {getDirectionBadge(sub.direction)}
                    {getStatusBadge(sub.status)}
                  </div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {formatDate(sub.transferDate)} {sub.transferTime}
                  </span>
                </div>

                <div className="text-sm text-gray-700 dark:text-gray-300 space-y-1 mt-2">
                  <p><span className="font-medium">{t('formSubmissions.airport')}:</span> {sub.airport}</p>
                  <p><span className="font-medium">{t('formSubmissions.location')}:</span> {sub.location}</p>
                  <p><span className="font-medium">{t('formSubmissions.guestCount')}:</span> {sub.guestCount}</p>
                  <p><span className="font-medium">{t('formSubmissions.filledBy')}:</span> {sub.filledBy || '-'}</p>
                  {sub.externalRefNo && (
                    <p><span className="font-medium">{t('formSubmissions.externalRefNo')}:</span> {sub.externalRefNo}</p>
                  )}
                  {sub.formSource && (
                    <p><span className="font-medium">{t('formSubmissions.formSource')}:</span> {sub.formSource}</p>
                  )}
                </div>
                <div className="mt-3">
                  {sub.createdTransferId ? (
                    <button
                      onClick={() => navigate(`/app/transfers?view=${sub.createdTransferId}`)}
                      className="w-full bg-blue-600 text-white text-sm py-2 px-4 rounded-lg hover:bg-blue-700"
                    >
                      {t('formSubmissions.viewTransfer')}
                    </button>
                  ) : (
                    <button
                      onClick={() => handleRetryTransfer(sub.id)}
                      disabled={retryingId === sub.id}
                      className="w-full bg-orange-500 text-white text-sm py-2 px-4 rounded-lg hover:bg-orange-600 disabled:opacity-50"
                    >
                      {retryingId === sub.id ? t('common.loading') : t('formSubmissions.createTransfer')}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

    </div>
  );
};

